import { useState } from "react";
import { Loader2, Phone, Mail, CheckCircle, XCircle } from "lucide-react";
import { GetStaffId } from "../../adapters/utils";
import { Chip } from "@mui/material";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { useParams } from "react-router-dom";
import {
  useGetAllinformationByid,
  usePostAcceptRejectApplicationById,
} from "../../adapters/Requests";
import ApplicantPhoto from "./ApplicantPhoto";
import { useFlashMessageContext } from "../../contexts/FlashMessageContext";
import { applicationStatuses } from "../../utils/constants";

function FinalAssessment({ applicationId, onActionComplete }) {
  const [openAcceptDialog, setOpenAcceptDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openRejectionCommentDialog, setOpenRejectionCommentDialog] =
    useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [comment, setComment] = useState("");

  const params = useParams();
  const application_id = applicationId || params.application_id;

  const staffId = GetStaffId();
  const { data, isLoading, isError, refetch } = useGetAllinformationByid({
    application_id,
  });

  const status = data?.status;
  const AcceptRejectApplication = usePostAcceptRejectApplicationById();

  const { RETURNED, VERIFIED, APPROVED, REJECTED, INVALIDATED } =
    applicationStatuses;
  const { setOpenFlash, setSeverity, setFlashMessage } =
    useFlashMessageContext();

  if (isError) return <div>Error reload or go back</div>;

  const displayStatus = ["ACCEPTED", "DECLINED", "COMPLETED"].includes(status)
    ? APPROVED
    : status;

  const handleMarkStatus = async (status_new) => {
    setIsConfirming(true);
    const formData = { application_id, status: status_new };

    if ([RETURNED, REJECTED].includes(status_new)) {
      formData.comment = comment;
    }

    try {
      const response = await AcceptRejectApplication.mutateAsync({ formData });
      refetch();
      handleCommentDialogClosure();
      handleCloseAcceptDialog();
      handleCloseRejectDialog();
      setFlashMessage(response.message);
      setSeverity("success");
      setOpenFlash(true);
      onActionComplete?.();
    } catch (error) {
      console.error(error);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCommentDialogClosure = () => {
    setOpenRejectionCommentDialog(false);
    setComment("");
  };
  const handleCommentSubmit = () => {
    if (comment.length < 3) return;
    setOpenRejectDialog(true);
  };
  const handleOpenAcceptDialog = () => setOpenAcceptDialog(true);
  const handleCloseAcceptDialog = () => setOpenAcceptDialog(false);
  const handleCloseRejectDialog = () => setOpenRejectDialog(false);
  const handleRejectConfirm = () =>
    handleMarkStatus(status === VERIFIED ? REJECTED : RETURNED);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 w-full">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
        </div>
      ) : data ? (
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <ApplicantPhoto applicationId={application_id} />

          <div className="flex flex-col items-end gap-3 w-full md:w-auto">
            <div className="flex items-center gap-2">
              {displayStatus === VERIFIED ? (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={handleOpenAcceptDialog}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600"
                    onClick={() => setOpenRejectionCommentDialog(true)}
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Reject
                  </Button>
                </div>
              ) : displayStatus === INVALIDATED ? (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => handleOpenAcceptDialog(true)}
                  >
                    Agree
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => setOpenRejectionCommentDialog(true)}
                  >
                    Disagree
                  </Button>
                </div>
              ) : (
                <Chip
                  label={displayStatus}
                  color={displayStatus === APPROVED ? "success" : "error"}
                  variant="filled"
                />
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-green-500" />
                <span className="font-medium">
                  {data.Applicant.phone_number}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-green-500" />
                <span className="font-medium">{data.Applicant.email}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-8 text-center text-gray-500">No data available</div>
      )}

      <Dialog open={openAcceptDialog} onOpenChange={handleCloseAcceptDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {status === VERIFIED ? "Confirm Approval" : "Confirmation"}
            </DialogTitle>
            <DialogDescription>
              {status === VERIFIED
                ? "Are you sure you want to approve this application?"
                : "Are you sure you want to agree with the verifier's decision? This will reject the application"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCloseAcceptDialog}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() =>
                handleMarkStatus(status === VERIFIED ? APPROVED : REJECTED)
              }
              disabled={isConfirming}
            >
              {isConfirming && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openRejectDialog} onOpenChange={setOpenRejectDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {status === VERIFIED
                ? "Confirm Rejection"
                : "Confirm Disagreement"}
            </DialogTitle>
            <DialogDescription>
              {status === VERIFIED
                ? "Are you sure you want to reject this application?"
                : "Are you sure you want to disagree? This will return the application to verification."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCloseRejectDialog}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleRejectConfirm}
              disabled={isConfirming}
            >
              {isConfirming && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openRejectionCommentDialog}
        onOpenChange={setOpenRejectionCommentDialog}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {status === VERIFIED
                ? "Rejection Comment"
                : "Disagreement Comment"}
            </DialogTitle>
            <DialogDescription>Please provide your reason.</DialogDescription>
          </DialogHeader>
          <textarea
            rows={4}
            className="w-full resize-none border border-slate-200 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Enter comment..."
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCommentDialogClosure}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleCommentSubmit}
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FinalAssessment;
